/**
 * Thin REST client for the Insights backend. Every Ops Command data call goes
 * through the `POST /api/call` dispatcher. Requests carry the httpOnly session
 * cookie (`credentials: 'include'`); a 401 means the session is missing or
 * expired and triggers the registered unauthorized handler (see auth.tsx).
 *
 * In dev, Vite proxies `/api` to the backend (see vite.config.ts), so
 * `VITE_API_BASE` can stay empty.
 *
 * Browser-owned config (scope groups + North Star) is attached as
 * `clientConfig` on every call so the server can resolve filters without Mongo.
 * The view context of the screen on display rides along as `viewContext`
 * (lib/requestContext.ts): venues, period, comparison, filters and screen.
 */
import { localeHeaders } from '../i18n';
import i18n from '../i18n';
import { buildClientConfigPayload } from './clientConfig';
import { getRequestViewContext } from './requestContext';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

export class ApiError extends Error {
  code?: string;
  status?: number;
  constructor(message: string, code?: string, status?: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

/** Thrown when the browser aborts a superseded `/api/call` (filter/scope change). */
export class AbortApiError extends Error {
  constructor(message = i18n.t('errors:requestAborted')) {
    super(message);
    this.name = 'AbortApiError';
  }
}

let unauthorizedHandler: (() => void) | null = null;

/** Register a callback invoked whenever the backend answers 401. */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

/** Notify the app that the current session is no longer valid. */
export function notifyUnauthorized(): void {
  unauthorizedHandler?.();
}

function restaurantIdFromArgs(args: readonly unknown[]): string | null {
  const first = args[0];
  return typeof first === 'string' && first ? first : null;
}

function requestBody(
  method: string,
  args: readonly unknown[],
  clientConfig: ReturnType<typeof buildClientConfigPayload>,
  demo: string | null | undefined,
): Record<string, unknown> {
  const viewContext = getRequestViewContext();
  return {
    method,
    args,
    clientConfig,
    ...(viewContext ? { viewContext } : {}),
    ...(demo ? { demo } : {}),
  };
}

/**
 * `demo` names Growth's demo group (fictional data): the server then answers
 * from the demo instead of the person's venues, and only for the read
 * methods the demo offers. Without it the request is exactly as before.
 */
export async function callMethod<T>(
  method: string,
  args: readonly unknown[],
  signal?: AbortSignal,
  demo?: string | null,
): Promise<T> {
  const restaurantId = restaurantIdFromArgs(args);
  const clientConfig = restaurantId
    ? buildClientConfigPayload(restaurantId)
    : buildClientConfigPayload('');

  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api/call`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...localeHeaders(),
      },
      body: JSON.stringify(requestBody(method, args, clientConfig, demo)),
      signal,
    });
  } catch (err) {
    if (
      (err instanceof DOMException && err.name === 'AbortError') ||
      (err instanceof Error && err.name === 'AbortError')
    ) {
      throw new AbortApiError();
    }
    throw err;
  }

  if (res.status === 401) {
    notifyUnauthorized();
    throw new ApiError(i18n.t('errors:sessionExpired'), 'unauthenticated', 401);
  }

  let body: { result?: T; error?: string; code?: string } = {};
  try {
    body = await res.json();
  } catch {
    // Non-JSON response (e.g. proxy error) - fall through to status handling.
  }

  if (!res.ok || body.error) {
    throw new ApiError(
      body.error ?? i18n.t('errors:requestFailed', { status: res.status }),
      body.code,
      res.status,
    );
  }

  return body.result as T;
}
